/**
 * The quick actor's interaction clock, client half (#582 S3, decision D-2).
 *
 * The server renews a display actor to "receipt time + 30 s" when the display reports ONE
 * deliberate interaction (`POST /api/profiles/actor/interaction`). What counts is the server's
 * list (`server/schemas/profile.schemas.js` `DEVICE_ACTOR_INTERACTION_KINDS`). What this module
 * guarantees is the other half — the rules a client must keep so that a board left alone still
 * falls back to ambient 30 seconds after the last touch:
 *
 *  - It sends NOTHING by itself. `report()` is called from a real user event handler only; there
 *    is no heartbeat, no interval and no call from polling, focus or a socket event.
 *  - Every report carries a fresh `interactionId`, so the server can refuse a retry of the SAME
 *    interaction as a renewal.
 *  - At most one request per second. Input that arrives faster is coalesced, and the LAST real
 *    interaction inside that second is sent when the second ends (the design's "trailing real
 *    interaction"). The trailing send defers a real event; it never invents one.
 *  - `clear()` drops anything waiting, so an actor that ended cannot be renewed by a queued report.
 */

export const INTERACTION_KINDS = Object.freeze(['activate-control', 'hold-start', 'open-details', 'scroll']);
export const MIN_INTERVAL_MS = 1_000;

function freshId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `i-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * @param {object} options
 * @param {(body: {generation: string, interactionId: string, kind: string}) => unknown} options.send
 * @param {() => number} [options.now]
 * @param {(fn: () => void, ms: number) => unknown} [options.schedule]
 * @param {(handle: unknown) => void} [options.cancel]
 */
export function createInteractionReporter({
  send,
  now = () => Date.now(),
  schedule = (fn, ms) => setTimeout(fn, ms),
  cancel = (handle) => clearTimeout(handle),
}) {
  let lastSentAt = -Infinity;
  let trailing = null;
  let timer = null;

  function flush(pending) {
    lastSentAt = now();
    send({ generation: pending.generation, interactionId: freshId(), kind: pending.kind });
  }

  return {
    /** Call from a real user event only. Returns true when a request went out now. */
    report(generation, kind) {
      if (typeof generation !== 'string' || !generation || !INTERACTION_KINDS.includes(kind)) return false;
      const wait = lastSentAt + MIN_INTERVAL_MS - now();
      if (wait <= 0 && !timer) {
        flush({ generation, kind });
        return true;
      }
      trailing = { generation, kind };
      if (!timer) {
        timer = schedule(() => {
          timer = null;
          const pending = trailing;
          trailing = null;
          if (pending) flush(pending);
        }, Math.max(0, wait));
      }
      return false;
    },
    clear() {
      trailing = null;
      if (timer) cancel(timer);
      timer = null;
    },
  };
}
